/*
 * ATBT global behaviour. Loaded on every page from layout/theme.liquid (defer).
 *
 * - Header: one-at-a-time desktop dropdowns, mobile menu toggle, Escape / outside-click handling.
 * - Search dialog (#search-dialog): predictive search via /search/suggest.json, debounced,
 *   stale requests aborted, keyboard friendly.
 * - Quick view (#quick-dialog): /products/{handle}.js, real variant select, Add to bag
 *   (/cart/add.js), cart count refresh, focus return, backdrop + Escape close.
 * - Pointer tilt for [data-tilt] (re-initialised on shopify:section:load).
 *
 * Delegated hooks:  [data-quick="product-handle"]  opens quick view
 *                   [data-open-search]             opens the search dialog
 * Events dispatched on document:  atbt:cart:added  { items, variantId }
 *                                 atbt:cart:updated { cart }
 * Public API: window.ATBT = { quickView, openSearch, formatMoney, refreshCartCount }
 */
(() => {
  'use strict';

  if (window.ATBT && typeof window.ATBT.quickView === 'function') return;

  const doc = document;

  /* ------------------------------------------------------------------ config */

  const DEFAULT_STRINGS = {
    priceFrom: 'From',
    seeOptions: 'See options',
    searchAll: 'Search the full store',
    searchResults: 'View all results for “[query]”',
    searchEmpty: 'No matching products. Try another word or explore all collections.',
    searchLoading: 'Searching…',
    searchCount: '[count] products found',
    searchError: 'Search is taking a break. Press Enter to search the full store.',
    qvLoading: 'Loading…',
    qvLoadError: "Sorry, this product couldn't be loaded. Please open the product page instead.",
    qvChoose: 'Choose your model, color or size',
    qvAdd: 'Add to bag',
    qvSoldOut: 'Sold out',
    qvInStock: 'In stock',
    qvOutOfStock: 'Currently sold out',
    qvDetails: 'View full details',
    qvFine: '',
    qvAdded: 'Added to your bag.',
    qvViewBag: 'View bag',
    qvError: "Sorry, we couldn't add that to your bag. Please try again.",
    qvNoPrice: 'See the product page for price',
    cartCountOne: '1 item',
    cartCountOther: '[count] items'
  };

  let configCache = null;
  let configSource = null;

  function getConfig() {
    const el = doc.getElementById('atbt-config');
    if (configCache && el === configSource) return configCache;

    let parsed = {};
    if (el) {
      try {
        parsed = JSON.parse(el.textContent) || {};
      } catch (error) {
        parsed = {};
      }
    }

    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    let root = (parsed.routes && parsed.routes.root) || shopifyRoot || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';

    const routes = Object.assign(
      {
        search: root + 'search',
        predictiveSearch: root + 'search/suggest',
        cart: root + 'cart',
        cartAdd: root + 'cart/add',
        allProducts: root + 'collections/all'
      },
      parsed.routes || {}
    );
    routes.root = root;

    const strings = Object.assign({}, DEFAULT_STRINGS);
    const custom = parsed.strings || {};
    Object.keys(custom).forEach((key) => {
      if (typeof custom[key] === 'string' && (custom[key] !== '' || key === 'qvFine')) strings[key] = custom[key];
    });

    configCache = {
      routes,
      strings,
      moneyFormat: parsed.moneyFormat || '${{amount}}',
      moneyWithCurrencyFormat: parsed.moneyWithCurrencyFormat || parsed.moneyFormat || '${{amount}} USD'
    };
    configSource = el;
    return configCache;
  }

  /* --------------------------------------------------------------- utilities */

  const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const escapeHTML = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ESCAPES[char]);
  const fill = (template, token, value) => String(template == null ? '' : template).split(token).join(String(value));
  const matchMediaSafe = (query) => (window.matchMedia ? window.matchMedia(query) : null);
  const isElement = (node) => node instanceof Element;

  function decodeEntities(text) {
    if (String(text).indexOf('&') === -1) return String(text);
    const area = doc.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  }

  function plainText(html, maxLength) {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    const text = (parsed.body ? parsed.body.textContent : '').replace(/\s+/g, ' ').trim();
    if (!maxLength || text.length <= maxLength) return text;
    const cut = text.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,.;:!-]+$/, '') + '…';
  }

  function resizeImage(url, width) {
    if (!url) return '';
    let src = String(url);
    if (src.indexOf('//') === 0) src = window.location.protocol + src;
    try {
      const parsed = new URL(src, window.location.origin);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(parsed.href)) parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return src;
    }
  }

  function fetchJSON(url, options) {
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { Accept: 'application/json' } }, options || {})).then(
      (response) => {
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        return response.json();
      }
    );
  }

  const newController = () => ('AbortController' in window ? new AbortController() : null);

  /* ------------------------------------------------------------------- money */

  const MONEY_FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ''],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };

  /**
   * Format an amount in cents with the shop's money format (plain text, no HTML).
   * Uses Shopify.formatMoney when a theme or app provides it.
   */
  function formatMoney(cents, format) {
    const template = format || getConfig().moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return decodeEntities(String(window.Shopify.formatMoney(cents, template)).replace(/<[^>]*>/g, ''));
      } catch (error) {
        /* fall through to the built-in formatter */
      }
    }

    const amount = Number(typeof cents === 'string' ? cents.replace('.', '') : cents);
    if (!isFinite(amount)) return '';

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = String(template).match(placeholder);
    if (!match) return '$' + (amount / 100).toFixed(2);

    const spec = MONEY_FORMATS[match[1]] || MONEY_FORMATS.amount;
    const parts = (Math.abs(amount) / 100).toFixed(spec[0]).split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, spec[1]);
    const number = (amount < 0 ? '-' : '') + (parts[1] ? whole + spec[2] + parts[1] : whole);

    return decodeEntities(String(template).replace(placeholder, number).replace(/<[^>]*>/g, ''));
  }

  /* ----------------------------------------------------------------- dialogs */

  const dialogState = new WeakMap();
  let scrollLocked = false;
  let previousOverflow = '';
  let pointerDownOnBackdrop = false;

  const openAtbtDialog = () => doc.querySelector('dialog.atbt-dialog[open]');

  function lockScroll() {
    if (scrollLocked) return;
    previousOverflow = doc.body.style.overflow;
    doc.body.style.overflow = 'hidden';
    scrollLocked = true;
  }

  function unlockScroll() {
    if (!scrollLocked || openAtbtDialog()) return;
    doc.body.style.overflow = previousOverflow;
    scrollLocked = false;
  }

  function openDialog(dialog, opener) {
    if (!dialog) return;

    doc.querySelectorAll('dialog.atbt-dialog[open]').forEach((other) => {
      if (other !== dialog) closeDialog(other, { restoreFocus: false });
    });
    closeNavDropdowns();
    closeMobileMenu();

    if (!dialog.open) {
      const returnFocus = isElement(opener) ? opener : doc.activeElement;
      dialogState.set(dialog, { returnFocus });
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
        } catch (error) {
          dialog.setAttribute('open', '');
        }
      } else {
        dialog.setAttribute('open', '');
      }
    }
    lockScroll();
  }

  function closeDialog(dialog, options) {
    if (!dialog || !dialog.open) return;
    if (options && options.restoreFocus === false) {
      const state = dialogState.get(dialog);
      if (state) state.returnFocus = null;
    }
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    // Clean up now instead of waiting for the async "close" event (it can lag behind a re-open).
    handleDialogClosed(dialog);
  }

  // Runs once per open/close cycle: from closeDialog() or from the native "close" event (Escape).
  function handleDialogClosed(dialog) {
    if (dialog.open) return; // a late "close" event for a dialog that has been re-opened since
    const state = dialogState.get(dialog);
    if (!state) {
      unlockScroll();
      return;
    }
    dialogState.delete(dialog);
    if (dialog.id === 'quick-dialog') abortQuickView();
    if (dialog.id === 'search-dialog') abortSearch();
    unlockScroll();

    const target = state.returnFocus;
    if (target && target !== doc.body && doc.contains(target) && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }
  }

  function isOutside(dialog, event) {
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  /* -------------------------------------------------------------- navigation */

  function closeNavDropdowns(except) {
    doc.querySelectorAll('.atbt .nav details[open]').forEach((details) => {
      if (details !== except) details.open = false;
    });
  }

  function setMobileMenu(open, options) {
    const menu = doc.getElementById('mobile-menu');
    const toggle = doc.getElementById('menu-toggle');
    if (!menu) return;

    menu.classList.toggle('open', open);
    if (!toggle) return;

    toggle.setAttribute('aria-expanded', String(open));
    const label = toggle.getAttribute(open ? 'data-label-close' : 'data-label-open');
    if (label) toggle.setAttribute('aria-label', label);
    const use = toggle.querySelector('use');
    if (use) use.setAttribute('href', open ? '#i-close' : '#i-menu');
    if (!open && options && options.focusToggle) toggle.focus();
  }

  function closeMobileMenu(options) {
    const menu = doc.getElementById('mobile-menu');
    if (menu && menu.classList.contains('open')) setMobileMenu(false, options);
  }

  /* ------------------------------------------------------------------ search */

  const searchState = { timer: 0, controller: null, cache: new Map(), query: '' };

  function searchElements() {
    return {
      dialog: doc.getElementById('search-dialog'),
      input: doc.getElementById('search-input'),
      results: doc.getElementById('search-results'),
      status: doc.getElementById('search-status'),
      all: doc.getElementById('search-store'),
      initial: doc.getElementById('search-initial')
    };
  }

  function abortSearch() {
    window.clearTimeout(searchState.timer);
    searchState.timer = 0;
    if (searchState.controller) searchState.controller.abort();
    searchState.controller = null;
  }

  function setSearchStatus(text) {
    const { status } = searchElements();
    if (status) status.textContent = text || '';
  }

  function setSearchLoading(loading) {
    const { results } = searchElements();
    if (!results) return;
    results.classList.toggle('is-loading', loading);
    results.setAttribute('aria-busy', String(loading));
  }

  function updateSearchAllLink(query) {
    const { all } = searchElements();
    if (!all) return;
    const { routes, strings } = getConfig();
    const label = all.querySelector('[data-search-all-label]') || all;
    if (query) {
      all.href = routes.search + '?q=' + encodeURIComponent(query) + '&options%5Bprefix%5D=last';
      label.textContent = fill(strings.searchResults, '[query]', query);
    } else {
      all.href = all.getAttribute('data-href-all') || routes.allProducts;
      label.textContent = strings.searchAll;
    }
  }

  function suggestionPrice(product) {
    const { strings } = getConfig();
    // Predictive search returns prices as decimal strings ("19.99"); numbers are treated as cents.
    const toCents = (value) => {
      if (typeof value === 'number') return Math.round(value);
      const number = parseFloat(value);
      return isFinite(number) ? Math.round(number * 100) : 0;
    };
    const min = toCents(product.price_min != null ? product.price_min : product.price);
    const max = toCents(product.price_max != null ? product.price_max : product.price);
    if (max <= 0) return strings.seeOptions;
    return (max > min ? strings.priceFrom + ' ' : '') + formatMoney(min);
  }

  function searchItemHTML(product) {
    const featured = product.featured_image;
    const image = featured && featured.url ? featured.url : typeof product.image === 'string' ? product.image : '';
    const media = image
      ? `<img src="${escapeHTML(resizeImage(image, 160))}" alt="" width="68" height="76" loading="lazy">`
      : '<span class="search-item-ph" aria-hidden="true"></span>';
    return (
      `<a class="search-item" href="${escapeHTML(product.url || '#')}">${media}` +
      `<span>${escapeHTML(product.title)}<small>${escapeHTML(suggestionPrice(product))}</small></span></a>`
    );
  }

  function renderSearchMessage(message) {
    const { results } = searchElements();
    if (!results) return;
    setSearchLoading(false);
    results.innerHTML = `<p class="empty">${escapeHTML(message)}</p>`;
    setSearchStatus(message);
  }

  function renderSearchResults(products) {
    const { results } = searchElements();
    if (!results) return;
    const { strings } = getConfig();
    if (!products.length) {
      renderSearchMessage(strings.searchEmpty);
      return;
    }
    setSearchLoading(false);
    results.innerHTML = products.map(searchItemHTML).join('');
    setSearchStatus(fill(strings.searchCount, '[count]', products.length));
  }

  function showInitialResults() {
    const { results, initial } = searchElements();
    if (!results) return;
    setSearchLoading(false);
    results.innerHTML = '';
    if (initial && initial.content) results.appendChild(initial.content.cloneNode(true));
  }

  function fetchSuggestions(query) {
    abortSearch();
    const controller = newController();
    searchState.controller = controller;
    const { routes, strings } = getConfig();
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '8');
    params.set('resources[options][unavailable_products]', 'last');
    setSearchStatus(strings.searchLoading);

    fetchJSON(routes.predictiveSearch + '.json?' + params.toString(), { signal: controller ? controller.signal : undefined })
      .then((data) => {
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        const key = query.toLowerCase();
        searchState.cache.set(key, products);
        if (searchState.cache.size > 40) searchState.cache.delete(searchState.cache.keys().next().value);
        if (searchState.query === query) renderSearchResults(products);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        if (searchState.query === query) renderSearchMessage(strings.searchError);
      })
      .finally(() => {
        if (searchState.controller === controller) searchState.controller = null;
      });
  }

  function updateSearch(rawQuery, options) {
    const { results } = searchElements();
    if (!results) return;
    const query = String(rawQuery || '').trim();
    searchState.query = query;
    window.clearTimeout(searchState.timer);
    updateSearchAllLink(query);

    if (!query) {
      abortSearch();
      showInitialResults();
      setSearchStatus('');
      return;
    }

    const cached = searchState.cache.get(query.toLowerCase());
    if (cached) {
      abortSearch();
      renderSearchResults(cached);
      return;
    }

    setSearchLoading(true);
    const delay = options && options.immediate ? 0 : 200;
    searchState.timer = window.setTimeout(() => fetchSuggestions(query), delay);
  }

  /**
   * Open the search dialog. openSearch('lamp') pre-fills a query.
   * Safe to pass as an event listener (an Event argument is ignored).
   */
  function openSearch(query, options) {
    const opts = options || {};
    const els = searchElements();
    const hasQuery = typeof query === 'string';

    if (!els.dialog || !els.input) {
      const { routes } = getConfig();
      window.location.href = routes.search + (hasQuery && query ? '?q=' + encodeURIComponent(query) : '');
      return;
    }

    const eventTrigger = query && isElement(query.currentTarget) ? query.currentTarget : null;
    if (hasQuery) els.input.value = query;
    openDialog(els.dialog, opts.trigger || eventTrigger || doc.activeElement);
    updateSearch(els.input.value, { immediate: true });
    els.input.focus();
    if (els.input.value) els.input.select();
  }

  function handleSearchArrows(event) {
    const { input, results } = searchElements();
    if (!input || !results) return;
    const items = Array.prototype.slice.call(results.querySelectorAll('a.search-item'));
    if (!items.length) return;

    if (event.target === input) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        items[0].focus();
      }
      return;
    }

    const index = items.indexOf(event.target);
    if (index === -1) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      items[Math.min(index + 1, items.length - 1)].focus();
    } else if (index === 0) {
      input.focus();
    } else {
      items[index - 1].focus();
    }
  }

  /* -------------------------------------------------------------- quick view */

  const quickState = { controller: null, cache: new Map(), token: 0 };
  const quickForms = new WeakMap();

  function abortQuickView() {
    if (quickState.controller) quickState.controller.abort();
    quickState.controller = null;
  }

  function extractHandle(value) {
    const text = String(value == null ? '' : value).trim();
    const match = text.match(/\/products\/([^/?#]+)/);
    return (match ? match[1] : text).replace(/\.js$/, '');
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  const productPath = (handle) => getConfig().routes.root + 'products/' + encodeURIComponent(safeDecode(handle));

  function loadProduct(handle) {
    if (quickState.cache.has(handle)) return Promise.resolve(quickState.cache.get(handle));
    const controller = newController();
    quickState.controller = controller;
    return fetchJSON(productPath(handle) + '.js', { signal: controller ? controller.signal : undefined })
      .then((product) => {
        quickState.cache.set(handle, product);
        return product;
      })
      .finally(() => {
        if (quickState.controller === controller) quickState.controller = null;
      });
  }

  function productImage(product, variant) {
    const media = variant && variant.featured_image;
    if (media && media.src) return { src: media.src, alt: media.alt || product.title };
    const src = product.featured_image || (Array.isArray(product.images) && product.images[0]) || '';
    const firstMedia = Array.isArray(product.media) ? product.media[0] : null;
    return src ? { src, alt: (firstMedia && firstMedia.alt) || product.title } : null;
  }

  function withVariant(url, variantId) {
    if (!variantId) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + encodeURIComponent(variantId);
  }

  function setQuickMessage(element, type, html) {
    if (!element) return;
    element.classList.remove('is-success', 'is-error');
    if (type) element.classList.add('is-' + type);
    element.setAttribute('role', type === 'error' ? 'alert' : 'status');
    element.innerHTML = html || '';
  }

  function applyVariant(form, variant) {
    const state = quickForms.get(form);
    if (!state) return;
    const { strings, moneyWithCurrencyFormat } = getConfig();
    const content = form.closest('.quick-content');
    const layout = form.closest('.quick-layout');
    if (!content) return;

    const price = content.querySelector('.quick-price');
    const stock = content.querySelector('.quick-status');
    const button = form.querySelector('[data-quick-add]');
    const buttonLabel = form.querySelector('[data-quick-add-label]');
    const idInput = form.querySelector('[name="id"]');
    const fullLink = content.querySelector('.quick-full');
    const message = content.querySelector('.quick-message');
    const available = Boolean(variant && variant.available);

    state.variant = variant || null;
    if (idInput && variant) idInput.value = String(variant.id);

    if (price) {
      if (variant && !(Number(variant.price) > 0)) {
        // $0 options (the design's "See store for price" case) never show "$0.00".
        price.textContent = strings.qvNoPrice;
      } else if (variant) {
        const compare = Number(variant.compare_at_price) || 0;
        price.innerHTML =
          escapeHTML(formatMoney(variant.price, moneyWithCurrencyFormat)) +
          (compare > variant.price ? ` <s>${escapeHTML(formatMoney(compare))}</s>` : '');
      } else {
        price.textContent = '';
      }
    }
    if (stock) stock.textContent = available ? strings.qvInStock : strings.qvOutOfStock;
    if (button) {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    }
    if (buttonLabel) buttonLabel.textContent = available ? strings.qvAdd : strings.qvSoldOut;
    if (fullLink) fullLink.href = withVariant(state.url, variant && variant.id);
    setQuickMessage(message, '', '');

    const image = layout && layout.querySelector('img.quick-image');
    const media = variant && variant.featured_image;
    if (image && media && media.src) {
      image.src = resizeImage(media.src, 900);
      image.alt = media.alt || state.product.title;
    }
  }

  function renderQuickView(body, product, options) {
    const { strings, routes } = getConfig();
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const requested = options.variantId ? variants.find((v) => String(v.id) === String(options.variantId)) : null;
    const current = requested || variants.find((v) => v.available) || variants[0] || null;
    const single = variants.length <= 1;
    const url = product.url || routes.root + 'products/' + product.handle;
    const category = options.category || product.type || '';
    const description = plainText(product.description, 260);
    const image = productImage(product, current);

    const variantField = single
      ? `<input type="hidden" name="id" value="${current ? escapeHTML(current.id) : ''}">`
      : `<label for="quick-variant">${escapeHTML(strings.qvChoose)}</label>` +
        `<select id="quick-variant" name="id">${variants
          .map(
            (v) =>
              `<option value="${escapeHTML(v.id)}"${current && v.id === current.id ? ' selected' : ''}>` +
              `${escapeHTML(v.title)}${v.available ? '' : ' · ' + escapeHTML(strings.qvSoldOut)}</option>`
          )
          .join('')}</select>`;

    body.innerHTML =
      '<div class="quick-layout">' +
      (image
        ? `<img class="quick-image" src="${escapeHTML(resizeImage(image.src, 900))}" alt="${escapeHTML(image.alt)}" width="900" height="900">`
        : '<div class="quick-image-placeholder" aria-hidden="true"></div>') +
      '<div class="quick-content">' +
      (category ? `<div class="eyebrow">${escapeHTML(category)}</div>` : '') +
      `<h2 id="quick-title">${escapeHTML(product.title)}</h2>` +
      '<p class="quick-price" id="quick-price"></p>' +
      '<p class="quick-status" id="quick-stock"></p>' +
      (description ? `<p class="quick-description">${escapeHTML(description)}</p>` : '') +
      '<form class="quick-form" data-quick-form novalidate>' +
      variantField +
      '<button type="submit" class="btn quick-add" data-quick-add><span data-quick-add-label></span>' +
      '<svg aria-hidden="true" focusable="false"><use href="#i-arrow"/></svg></button>' +
      '</form>' +
      '<p class="quick-message" id="quick-message" role="status" aria-live="polite"></p>' +
      `<a class="quick-full" id="quick-full" href="${escapeHTML(url)}">${escapeHTML(strings.qvDetails)}</a>` +
      (strings.qvFine ? `<p class="fine">${escapeHTML(strings.qvFine)}</p>` : '') +
      '</div></div>';

    const form = body.querySelector('[data-quick-form]');
    quickForms.set(form, { product, url, variant: current });
    applyVariant(form, current);
  }

  /**
   * Open the quick-view dialog for a product handle (a /products/... URL also works).
   * options: { trigger: Element (focus returns here), category: string, variantId: number }
   */
  function quickView(handle, options) {
    const opts = options || {};
    const productHandle = extractHandle(handle);
    if (!productHandle) return Promise.resolve(null);

    const dialog = doc.getElementById('quick-dialog');
    const body = doc.getElementById('quick-body');
    if (!dialog || !body) {
      window.location.href = productPath(productHandle);
      return Promise.resolve(null);
    }

    const { strings } = getConfig();
    const token = ++quickState.token;
    abortQuickView();
    body.innerHTML = `<p class="quick-loading" role="status"><span id="quick-title">${escapeHTML(strings.qvLoading)}</span></p>`;
    openDialog(dialog, opts.trigger || doc.activeElement);

    return loadProduct(productHandle)
      .then((product) => {
        if (token !== quickState.token || !dialog.open) return null;
        renderQuickView(body, product, opts);
        return product;
      })
      .catch((error) => {
        if ((error && error.name === 'AbortError') || token !== quickState.token) return null;
        body.innerHTML =
          '<div class="quick-loading quick-error" role="alert">' +
          `<p id="quick-title">${escapeHTML(strings.qvLoadError)}</p>` +
          `<a class="quick-full" href="${escapeHTML(productPath(productHandle))}">${escapeHTML(strings.qvDetails)}</a>` +
          '</div>';
        return null;
      });
  }

  function submitQuickAdd(form) {
    const idInput = form.querySelector('[name="id"]');
    const button = form.querySelector('[data-quick-add]');
    const content = form.closest('.quick-content');
    const message = content && content.querySelector('.quick-message');
    if (!idInput || !idInput.value || !button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const { routes, strings } = getConfig();
    const variantId = Number(idInput.value);
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    setQuickMessage(message, '', '');

    fetch(routes.cartAdd + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then((response) =>
        response
          .json()
          .catch(() => ({}))
          .then((data) => ({ ok: response.ok, data: data || {} }))
      )
      .then(({ ok, data }) => {
        if (!ok || data.status) {
          const error = new Error('Add to cart failed');
          error.userMessage = typeof data.description === 'string' ? data.description : '';
          throw error;
        }
        setQuickMessage(
          message,
          'success',
          `${escapeHTML(strings.qvAdded)} <a href="${escapeHTML(routes.cart)}">${escapeHTML(strings.qvViewBag)}</a>`
        );
        doc.dispatchEvent(new CustomEvent('atbt:cart:added', { detail: { items: data.items || [data], variantId } }));
        return refreshCartCount().catch(() => null);
      })
      .catch((error) => {
        setQuickMessage(message, 'error', escapeHTML((error && error.userMessage) || strings.qvError));
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      });
  }

  /* -------------------------------------------------------------------- cart */

  function renderCartBubble(count) {
    const link = doc.getElementById('cart-icon-bubble');
    if (!link) return;
    const { strings } = getConfig();
    let bubble = link.querySelector('.cart-count-bubble');

    if (count <= 0) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = doc.createElement('span');
      bubble.className = 'cart-count-bubble';
      link.appendChild(bubble);
    }
    const label = fill(count === 1 ? strings.cartCountOne : strings.cartCountOther, '[count]', count);
    bubble.innerHTML =
      (count < 100 ? `<span aria-hidden="true">${count}</span>` : '') + `<span class="visually-hidden">${escapeHTML(label)}</span>`;
  }

  /** Re-read /cart.js, update the header bag count and return the cart. */
  function refreshCartCount() {
    return fetchJSON(getConfig().routes.cart + '.js').then((cart) => {
      renderCartBubble(Number(cart && cart.item_count) || 0);
      doc.dispatchEvent(new CustomEvent('atbt:cart:updated', { detail: { cart } }));
      return cart;
    });
  }

  /* -------------------------------------------------------------------- tilt */

  const pointerQuery = matchMediaSafe('(hover: hover) and (pointer: fine)');
  const reducedQuery = matchMediaSafe('(prefers-reduced-motion: reduce)');
  const tiltState = new WeakMap();
  const activeTilts = new Set();

  const tiltEnabled = () => (!pointerQuery || pointerQuery.matches) && !(reducedQuery && reducedQuery.matches);

  function resetTilt(card) {
    const state = tiltState.get(card);
    if (state) {
      if (state.frame) cancelAnimationFrame(state.frame);
      state.frame = 0;
      state.bounds = null;
      state.point = null;
    }
    card.classList.remove('tilt-active');
    ['--tilt-x', '--tilt-y', '--shine-x', '--shine-y'].forEach((name) => card.style.removeProperty(name));
    activeTilts.delete(card);
  }

  function resetAllTilts() {
    Array.from(activeTilts).forEach(resetTilt);
  }

  function bindTilt(card) {
    if (tiltState.has(card)) return;
    const state = { frame: 0, bounds: null, point: null };
    tiltState.set(card, state);

    card.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch' || !tiltEnabled()) return;
      state.bounds = card.getBoundingClientRect();
      card.classList.add('tilt-active');
      activeTilts.add(card);
    });

    card.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch' || !tiltEnabled()) return;
      if (!state.bounds) state.bounds = card.getBoundingClientRect();
      state.point = { x: event.clientX, y: event.clientY };
      if (!card.classList.contains('tilt-active')) card.classList.add('tilt-active');
      activeTilts.add(card);
      if (state.frame) return;
      state.frame = requestAnimationFrame(() => {
        state.frame = 0;
        const bounds = state.bounds;
        const point = state.point;
        if (!bounds || !point || !bounds.width || !bounds.height || !tiltEnabled()) return;
        const x = Math.max(0, Math.min(1, (point.x - bounds.left) / bounds.width));
        const y = Math.max(0, Math.min(1, (point.y - bounds.top) / bounds.height));
        card.style.setProperty('--tilt-x', ((0.5 - y) * 11).toFixed(2) + 'deg');
        card.style.setProperty('--tilt-y', ((x - 0.5) * 11).toFixed(2) + 'deg');
        card.style.setProperty('--shine-x', (x * 100).toFixed(1) + '%');
        card.style.setProperty('--shine-y', (y * 100).toFixed(1) + '%');
      });
    });

    const reset = () => resetTilt(card);
    card.addEventListener('pointerleave', reset);
    card.addEventListener('pointercancel', reset);
    card.addEventListener('blur', reset);
  }

  function initTilt(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : doc;
    if (isElement(scope) && scope.matches('[data-tilt]')) bindTilt(scope);
    scope.querySelectorAll('[data-tilt]').forEach(bindTilt);
  }

  const onMediaChange = (query, handler) => {
    if (!query) return;
    if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
    else if (typeof query.addListener === 'function') query.addListener(handler);
  };

  /* ---------------------------------------------------------- event wiring */

  doc.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      pointerDownOnBackdrop = isElement(target) && target.matches('dialog.atbt-dialog[open]') && isOutside(target, event);
    },
    true
  );

  doc.addEventListener('click', (event) => {
    const target = event.target;
    if (!isElement(target)) return;

    if (target.matches('dialog.atbt-dialog[open]')) {
      if (pointerDownOnBackdrop && isOutside(target, event)) closeDialog(target);
      pointerDownOnBackdrop = false;
      return;
    }

    const closer = target.closest('dialog.atbt-dialog [data-close]');
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest('dialog'));
      return;
    }

    const quickTrigger = target.closest('[data-quick]');
    if (quickTrigger && quickTrigger.getAttribute('data-quick')) {
      event.preventDefault();
      const card = quickTrigger.closest('.shop-card');
      const categoryEl = card && card.querySelector('.shop-card-category');
      quickView(quickTrigger.getAttribute('data-quick'), {
        trigger: quickTrigger,
        category: categoryEl ? categoryEl.textContent.trim() : ''
      });
      return;
    }

    const searchTrigger = target.closest('[data-open-search]');
    if (searchTrigger) {
      event.preventDefault();
      const preset = searchTrigger.getAttribute('data-open-search');
      openSearch(preset ? preset : undefined, { trigger: searchTrigger });
      return;
    }

    if (target.closest('#menu-toggle')) {
      const menu = doc.getElementById('mobile-menu');
      setMobileMenu(!(menu && menu.classList.contains('open')));
      return;
    }

    if (target.closest('#mobile-menu a')) closeMobileMenu();
    if (!target.closest('.atbt .nav')) closeNavDropdowns();
  });

  // Only one desktop dropdown open at a time ("toggle" does not bubble, so listen in the capture phase).
  doc.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (isElement(details) && details.matches('.atbt .nav details') && details.open) closeNavDropdowns(details);
    },
    true
  );

  // Close a dropdown when keyboard focus leaves it.
  doc.addEventListener('focusout', (event) => {
    const details = isElement(event.target) ? event.target.closest('.atbt .nav details[open]') : null;
    const next = event.relatedTarget;
    if (details && isElement(next) && !details.contains(next)) details.open = false;
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      const openDetails = doc.querySelector('.atbt .nav details[open]');
      if (openDetails) {
        const hadFocus = openDetails.contains(doc.activeElement);
        closeNavDropdowns();
        const summary = openDetails.querySelector('summary');
        if (hadFocus && summary) summary.focus();
      }
      const menu = doc.getElementById('mobile-menu');
      if (menu && menu.classList.contains('open')) {
        const toggle = doc.getElementById('menu-toggle');
        const hadFocus = menu.contains(doc.activeElement) || doc.activeElement === toggle;
        setMobileMenu(false, { focusToggle: hadFocus });
      }
      return;
    }

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && isElement(event.target) && event.target.closest('#search-dialog')) {
      handleSearchArrows(event);
    }
  });

  doc.addEventListener('input', (event) => {
    if (isElement(event.target) && event.target.id === 'search-input') updateSearch(event.target.value);
  });

  doc.addEventListener('change', (event) => {
    const target = event.target;
    if (!isElement(target) || target.id !== 'quick-variant') return;
    const form = target.closest('[data-quick-form]');
    const state = form && quickForms.get(form);
    if (!state) return;
    const variant = (state.product.variants || []).find((v) => String(v.id) === target.value);
    applyVariant(form, variant || null);
  });

  doc.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isElement(form)) return;
    if (form.matches('[data-quick-form]')) {
      event.preventDefault();
      submitQuickAdd(form);
      return;
    }
    if (form.matches('[data-search-form]')) {
      const input = form.querySelector('input[name="q"]');
      if (input && !input.value.trim()) {
        event.preventDefault();
        input.focus();
      }
    }
  });

  // <dialog> fires "close" (non-bubbling) for Escape, form method=dialog and close().
  doc.addEventListener(
    'close',
    (event) => {
      if (isElement(event.target) && event.target.matches('dialog.atbt-dialog')) handleDialogClosed(event.target);
    },
    true
  );

  // Theme editor: re-bind tilt for re-rendered sections and never leave the page scroll-locked.
  doc.addEventListener('shopify:section:load', (event) => {
    initTilt(event.target);
    unlockScroll();
  });
  doc.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!isElement(target)) return;
    if (target.querySelector('#mobile-menu')) closeMobileMenu();
    if (target.querySelector('dialog.atbt-dialog[open]')) {
      abortSearch();
      abortQuickView();
      window.setTimeout(unlockScroll, 0);
    }
  });

  onMediaChange(pointerQuery, resetAllTilts);
  onMediaChange(reducedQuery, resetAllTilts);
  window.addEventListener(
    'scroll',
    () => {
      if (activeTilts.size) resetAllTilts();
    },
    { passive: true }
  );
  window.addEventListener(
    'resize',
    () => {
      if (activeTilts.size) resetAllTilts();
    },
    { passive: true }
  );

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => initTilt(doc));
  } else {
    initTilt(doc);
  }

  /* ------------------------------------------------------------- public API */

  window.ATBT = Object.assign(window.ATBT || {}, {
    quickView,
    openSearch,
    formatMoney,
    refreshCartCount
  });
})();
